import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Select, Button, message, Row, Col, Checkbox, Radio } from "antd";
import "./CreateAudienceModal.css";
import { fetchRegistrations, fetchPrograms } from "services/audienceService";

const { TextArea } = Input;
const { Option } = Select;

const CreateAudienceModal = ({ visible, onCancel, onSubmit, mode = "create", initialData = null, smsEnabled = false, token, clubId, availableTeams }) => {
  const [form] = Form.useForm();
  const [audienceChannel, setAudienceChannel] = useState("email");
  const [createFrom, setCreateFrom] = useState("");
  const [loading, setLoading] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selectedRegistration, setSelectedRegistration] = useState(undefined);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(undefined);

  // Helper to match an initial program name to a loaded program label robustly
  const findMatchingProgramLabel = (pname, loadedPrograms) => {
    if (!pname || !Array.isArray(loadedPrograms)) return null;
    const pNorm = String(pname).trim().toLowerCase();
    // exact match
    let found = loadedPrograms.find(lp => String(lp).trim().toLowerCase() === pNorm);
    if (found) return found;
    // substring matches
    found = loadedPrograms.find(lp => String(lp).trim().toLowerCase().includes(pNorm));
    if (found) return found;
    found = loadedPrograms.find(lp => pNorm.includes(String(lp).trim().toLowerCase()));
    if (found) return found;
    // compare alphanumeric only
    const alpha = (s) => String(s).replace(/[^a-z0-9]/gi, '').toLowerCase();
    const pAlpha = alpha(pNorm);
    if (pAlpha) {
      found = loadedPrograms.find(lp => alpha(lp) === pAlpha);
      if (found) return found;
    }
    return null;
  };
  useEffect(() => {
    if (!visible) {
      // Reset when modal closes
      setAudienceChannel("email");
      setCreateFrom("");
      setRegistrations([]);
      setPrograms([]);
      setSelectedRegistration(undefined);
      setTeams([]);
      setSelectedTeam(undefined);
      return;
    }
    
    // Reset first
    form.resetFields();

    // If editing, pre-fill fields
      if (mode === "edit" && initialData) {
      const createFromValue = initialData.createFrom || "";
      setCreateFrom(createFromValue);
      setAudienceChannel(initialData.channel || "email");

      const preset = {
        audienceChannel: initialData.channel || "email",
        audienceName: initialData.name || "",
        createFrom: createFromValue,
        emails: initialData.emails || "",
        phoneNumbers: initialData.phoneNumbers || "",
        registration: initialData.registrationIds || undefined,
        // For PROGRAM mode the form item is named `registrationForProgram`
        registrationForProgram: initialData.registrationIdForProgram || undefined,
        program: initialData.programIds || undefined,
        team: initialData.teamId || undefined,
      };
      
      form.setFieldsValue(preset);
      
      // Load registrations if needed for REGISTRATION or PROGRAM types
      if (createFromValue === "REGISTRATION" || createFromValue === "PROGRAM") {
        loadRegistrations().then((loadedRegs) => {          
          // After registrations are loaded, if we have registration IDs, set them
          if (initialData.registrationIds && Array.isArray(initialData.registrationIds)) {
            // Convert IDs to strings to match our normalized registration ids
            const normalizedIds = initialData.registrationIds.map(id => {
              const reg = loadedRegs.find(r => String(r.id) === String(id) || Number(r.id) === Number(id));
              return reg ? String(reg.id) : String(id);
            }).filter(Boolean);

          
            form.setFieldsValue({ registration: normalizedIds });
          }
          
          // For PROGRAM type, also load programs if we have a registration
          if (createFromValue === "PROGRAM") {
            const regForProgram = initialData.registrationIdForProgram;
            if (regForProgram) {
              // Ensure the registration select shows the preselected registration (as string)
              try {
                form.setFieldsValue({ registrationForProgram: String(regForProgram) });
              } catch (err) {
                // ignore if form not ready
              }
              setSelectedRegistration(String(regForProgram));
              onSelectRegistration(String(regForProgram)).then((loadedPrograms) => {
                // After programs are loaded, map initial program names to the loaded program labels
                if (initialData.programIds && Array.isArray(initialData.programIds) && initialData.programIds.length > 0) {
                  const selectedPrograms = initialData.programIds
                    .map(pname => {
                      if (!pname) return null;
                      const match = findMatchingProgramLabel(pname, loadedPrograms || []);
                      return match || String(pname).trim();
                    })
                    .filter(Boolean);
                  form.setFieldsValue({ program: selectedPrograms });
                } else if (Array.isArray(loadedPrograms) && loadedPrograms.length === 1) {
                  // Fallback: if we didn't get programIds but the registration only has one program,
                  // auto-select that program so modal shows the program checked.
                  form.setFieldsValue({ program: [loadedPrograms[0]] });
                }
              });
            } else {
              // Best-effort fallback: if backend didn't return registrationIdForProgram but
              // there is exactly one registration available for this club, auto-select it
              // so the modal displays programs and is usable.
              if (loadedRegs && Array.isArray(loadedRegs) && loadedRegs.length === 1) {
                const singleRegId = String(loadedRegs[0].id);
                try {
                  form.setFieldsValue({ registrationForProgram: singleRegId });
                } catch (err) {}
                setSelectedRegistration(singleRegId);
                onSelectRegistration(singleRegId).then((loadedPrograms) => {
                  if (initialData.programIds && Array.isArray(initialData.programIds) && initialData.programIds.length > 0) {
                    const selectedPrograms = initialData.programIds
                      .map(pname => {
                        if (!pname) return null;
                        const match = findMatchingProgramLabel(pname, loadedPrograms || []);
                        return match || String(pname).trim();
                      })
                      .filter(Boolean);
                    form.setFieldsValue({ program: selectedPrograms });
                  } else if (Array.isArray(loadedPrograms) && loadedPrograms.length === 1) {
                    form.setFieldsValue({ program: [loadedPrograms[0]] });
                  }
                });
              }
            }
          }
        });
      }
      if (createFromValue === "TEAM") {
        loadTeams();
        if (initialData.teamId) {
          setSelectedTeam(initialData.teamId);
        }
      }
    } else {
      setCreateFrom("");
    }
  }, [visible, form, mode, initialData]);

  const handleCreateFromChange = (value) => {
    setCreateFrom(value);
    // Clear dependent fields when changing create from option
    form.setFieldsValue({
      registration: undefined,
      program: undefined,
      emails: "",
      team: undefined,
    });
    setSelectedRegistration(undefined);
    setPrograms([]);
    setTeams([]);
    setSelectedTeam(undefined);
    if (value === "REGISTRATION" || value === "PROGRAM") {
      // lazy load registrations when switching to these modes
      loadRegistrations();
    }
    if (value === "TEAM") {
      loadTeams();
    }
  };

  const loadRegistrations = async () => {
    try {
      if (!clubId) {
        setRegistrations([]);
        return [];
      }
      const regs = await fetchRegistrations(token, clubId);
      // normalize if backend fields differ
      const normalized = (Array.isArray(regs) ? regs : []).map((r) => ({
        // Normalize IDs to string so Select and Checkbox values match form values reliably
        id: String(r.id ?? r.registration_id ?? r.ID),
        name: r.name ?? r.registration_name ?? r.Name,
      })).filter(x => x.id && x.name);
      setRegistrations(normalized);
      return normalized;
    } catch (e) {
      message.error("Failed to load registrations");
      return [];
    }
  };

  const onSelectRegistration = async (registrationId) => {
    setSelectedRegistration(registrationId);
    form.setFieldsValue({ program: undefined });
    try {
      const list = await fetchPrograms(token, registrationId);
      // get_programs returns distinct program names; normalize to {label, value}
      const normalized = (Array.isArray(list) ? list : []).map((p) => {
        const name = typeof p === "string" ? p : (p.name ?? p.registration_group_name ?? p.ProgramName);
        return name;
      }).filter(Boolean);
      setPrograms(normalized);
      return normalized;
    } catch (e) {
      message.error("Failed to load programs");
      setPrograms([]);
      return [];
    }
  };

  const loadTeams = () => {
    // Load teams from the availableTeams prop (passed from parent which has store access)
    const loginTeams = availableTeams;
    if (Array.isArray(loginTeams) && loginTeams.length > 0) {
      const normalized = loginTeams
        .map((t) => ({
          id: String(t.id ?? t.team_id ?? t.ID ?? ""),
          name: t.name ?? t.team_name ?? t.NAME ?? "",
        }))
        .filter((t) => t.id && t.name);
      setTeams(normalized);
    } else {
      setTeams([]);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // Process the form data based on create from option
      let processedData = {
        id: initialData?.id,
        name: values.audienceName,
        createFrom: values.createFrom,
        channel: audienceChannel,
      };

      if (values.createFrom === "MANUAL") {
        if (audienceChannel === "sms") {
          processedData.phoneNumbers = values.phoneNumbers;
        } else {
          processedData.emails = values.emails;
        }
      } else if (values.createFrom === "REGISTRATION") {
        // Map registration IDs to registration names for search_string
        // Backend expects registration names, not IDs
        const selectedRegIds = values.registration || [];
        const registrationNames = selectedRegIds
          .map(id => {
            const reg = registrations.find(r => String(r.id) === String(id));
            return reg?.name;
          })
          .filter(Boolean);
        processedData.registrationIds = selectedRegIds; // Keep IDs for reference if needed
        processedData.registrationNames = registrationNames; // Names for search_string
      } else if (values.createFrom === "PROGRAM") {
        // For PROGRAM, include selected registration and chosen program names
        // programs are already names (from fetchPrograms API)
        processedData.registrationIdForProgram = selectedRegistration;
        processedData.programIds = values.program; // program names array (already strings)
      } else if (values.createFrom === "TEAM") {
        // For TEAM, include the selected team id
        // TODO: fetch emails by team_id using the team email endpoint before creating
        processedData.teamId = values.team;
      }

      await onSubmit(processedData, mode);
      onCancel();
    } catch (error) {
      message.error(mode === "edit" ? "Failed to update audience. Please try again." : "Failed to create audience. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setAudienceChannel("email");
    setCreateFrom("");
    setTeams([]);
    setSelectedTeam(undefined);
    onCancel();
  };

  return (
    <Modal
      title={mode === "edit" ? "EDIT AUDIENCE" : "CREATE NEW AUDIENCE"}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      className="create-audience-modal"
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="audience-form"
      >
        {smsEnabled && (
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="AUDIENCE TYPE">
              <Radio.Group
                value={audienceChannel}
                onChange={(e) => {
                  setAudienceChannel(e.target.value);
                  form.setFieldsValue({ emails: "", phoneNumbers: "" });
                }}
              >
                <Radio value="email">Email</Radio>
                <Radio value="sms">SMS</Radio>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>
        )}

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="AUDIENCE NAME"
              name="audienceName"
              rules={[
                { required: true, message: "Please enter audience name" },
                { min: 2, message: "Audience name must be at least 2 characters" },
              ]}
            >
              <Input
                placeholder="Enter audience name"
                className="form-input"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="CREATE FROM"
              name="createFrom"
              rules={[{ required: true, message: "Please select create from option" }]}
            >
              <Select
                placeholder="Select create from option"
                onChange={handleCreateFromChange}
                className="form-select"
              >
                <Option value="REGISTRATION">REGISTRATION</Option>
                <Option value="PROGRAM">PROGRAM</Option>
                <Option value="MANUAL">MANUAL</Option>
                <Option value="TEAM">TEAM</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Conditional fields based on Create From selection */}
        {createFrom === "MANUAL" && audienceChannel === "email" && (
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="EMAILS"
                name="emails"
                validateTrigger="onBlur"
                rules={[
                  { required: true, message: "Please enter emails" },
                ]}
                extra="Enter comma-separated email addresses (e.g., email1@example.com, email2@example.com)"
              >
                <TextArea
                  rows={4}
                  placeholder="Enter comma-separated email addresses"
                  className="form-textarea"
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {createFrom === "MANUAL" && audienceChannel === "sms" && (
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="PHONE NUMBERS"
                name="phoneNumbers"
                validateTrigger="onBlur"
                rules={[
                  { required: true, message: "Please enter phone numbers" },
                ]}
                extra="Enter comma-separated phone numbers (e.g., +12125551234, +13105559876)"
              >
                <TextArea
                  rows={4}
                  placeholder="Enter comma-separated phone numbers"
                  className="form-textarea"
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {createFrom === "REGISTRATION" && (
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="SELECT REGISTRATION"
                name="registration"
                rules={[{ required: true, message: "Please select at least one registration" }]}
              >
                <Checkbox.Group className="registration-checkboxes">
                  <div className="registration-options">
                    {registrations.map((r) => {
                      // Ensure ID is consistently a string or number for matching
                      const regId = r.id ?? r.registration_id ?? r.ID;
                      return (
                        <Checkbox key={regId} value={regId}>{r.name}</Checkbox>
                      );
                    })}
                  </div>
                </Checkbox.Group>
              </Form.Item>
            </Col>
          </Row>
        )}

        {createFrom === "PROGRAM" && (
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="SELECT REGISTRATION"
                name="registrationForProgram"
                rules={[{ required: true, message: "Please select a registration" }]}
              >
                <Select
                  placeholder="Select a registration"
                  onChange={(val) => onSelectRegistration(val)}
                >
                  {registrations.map((r) => (
                    <Select.Option key={r.id} value={r.id}>{r.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="SELECT PROGRAM"
                name="program"
                rules={[{ required: true, message: "Please select at least one program" }]}
              >
                {selectedRegistration && programs.length === 0 ? (
                  <p style={{ color: "#888", fontSize: 13, margin: 0 }}>No Programs Found</p>
                ) : (
                  <Checkbox.Group className="program-checkboxes">
                    <div className="program-options">
                      {programs.map((name) => (
                        <Checkbox key={name} value={name}>{name}</Checkbox>
                      ))}
                    </div>
                  </Checkbox.Group>
                )}
              </Form.Item>
            </Col>
          </Row>
        )}

        {createFrom === "TEAM" && (
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="SELECT TEAM"
                name="team"
                rules={[{ required: true, message: "Please select a team" }]}
              >
                <Select
                  placeholder="Select a team"
                  onChange={(val) => setSelectedTeam(val)}
                >
                  {teams.map((t) => (
                    <Select.Option key={t.id} value={t.id}>
                      {t.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              {teams.length === 0 && (
                <p style={{ color: "#888", fontSize: 12, marginTop: -12, marginBottom: 16 }}>
                  No teams found in your login session. Teams are populated from the login response.
                </p>
              )}
            </Col>
          </Row>
        )}

        <Row gutter={16} className="form-actions">
          <Col span={24}>
            <div className="button-group">
              <Button
                onClick={handleCancel}
                className="cancel-button"
                size="large"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="submit-button"
                size="large"
              >
                {mode === "edit" ? "Save Changes" : "Create Audience"}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default CreateAudienceModal;
